import { getObjectBindingsPort, getObjectEventsPort, getPaletteForWire } from '../../Hasp/properties/v2/logic/index.js';
import { getHassNodeSchema, getHassObjectPorts, getHassPaletteForWire } from '../../Hasp/properties/v2/hass/index.js';
import { LOGIC_NODE_SIZE } from '../../logic/logicMigration.js';
import { HASS_NODE_SIZE } from '../../hass/hassLayout.js';
import { useLogicStore } from '../../stores/logicStore.js';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { LOGIC_PALETTE_MIME } from '../logic/paletteDrag.js';

// DataTransfer type of a Home Assistant palette entry dragged onto the canvas.
export const HASS_PALETTE_MIME = 'application/x-openhasp-hass-node';

const hassKind = entry => getHassNodeSchema(entry.nodeType)?.kind;

/**
 * How the canvas (App.vue) drives each graph domain: its editor store, its
 * layer on the stage, its palette drag type, the picker for a wire released
 * on empty canvas, and which selected or dropped-on thing anchors a new node
 * (see the domains' planPaletteNode).
 *
 *   anchorsObject(entry, object)  whether the selected object anchors a palette click
 *   dropAnchors(entry, target)    placement anchors for a drop (GraphLayer.dropTarget)
 */
export const CANVAS_GRAPHS = Object.freeze({
    logic: {
        key: 'logic',
        useStore: useLogicStore,
        layerOf: stage => stage?.logicLayer ?? null,
        mime: LOGIC_PALETTE_MIME,
        nodeSize: LOGIC_NODE_SIZE,
        paletteForWire: getPaletteForWire,
        // The page itself is an event source too (touch events, swipe gestures);
        // a GPIO binding drives an object.
        anchorsObject: (entry, object) => (entry.nodeType === 'gpioBinding'
            ? Boolean(getObjectBindingsPort(object))
            : entry.nodeType === 'event' && Boolean(getObjectEventsPort(object))),
        // An event or binding on an object or GPIO node; anything else on what runs it.
        dropAnchors: (entry, target) => (['event', 'gpioBinding'].includes(entry.nodeType)
            ? { anchorObject: target.objectUuid ? { uuid: target.objectUuid } : null, anchorNodeId: target.nodeAt.gpio ?? null }
            : { anchorObject: null, anchorNodeId: target.nodeAt.event ?? target.nodeAt.condition ?? target.nodeAt.switch ?? null }),
    },
    hass: {
        key: 'hass',
        useStore: useHassFlowStore,
        layerOf: stage => stage?.hassLayer ?? null,
        mime: HASS_PALETTE_MIME,
        nodeSize: HASS_NODE_SIZE,
        paletteForWire: getHassPaletteForWire,
        // Entities and bindings enter an object; a trigger listens to it.
        anchorsObject: (entry, object) => {
            const ports = getHassObjectPorts(object);
            const kind = hassKind(entry);
            return kind === 'trigger' ? ports.outputs.length > 0 : ['entity', 'binding'].includes(kind) && ports.inputs.length > 0;
        },
        dropAnchors: (entry, target) => {
            const onObject = target.objectUuid ? { uuid: target.objectUuid } : null;
            switch (hassKind(entry)) {
            case 'entity': return { anchorObject: onObject, anchorNodeId: onObject ? null : target.nodeAt.binding ?? target.nodeAt.condition ?? target.nodeAt.action ?? null };
            case 'binding': return { anchorObject: onObject, anchorNodeId: onObject ? null : target.nodeAt.switch ?? target.nodeAt.entity ?? null };
            case 'switch': return { anchorObject: null, anchorNodeId: target.nodeAt.entity ?? null };
            case 'trigger': return { anchorObject: onObject, anchorNodeId: onObject ? null : target.nodeAt.entity ?? null };
            case 'condition': return { anchorObject: null, anchorNodeId: target.nodeAt.trigger ?? null };
            case 'action': return { anchorObject: null, anchorNodeId: target.nodeAt.condition ?? target.nodeAt.trigger ?? target.nodeAt.entity ?? null };
            default: return { anchorObject: null, anchorNodeId: null };
            }
        },
    },
});

/** The graph domain whose palette entry a drag carries, or null. */
export function canvasGraphOfDrag(dataTransfer) {
    const types = dataTransfer?.types ?? [];
    return Object.values(CANVAS_GRAPHS).find(graph => types.includes(graph.mime)) ?? null;
}
